<template>
  <div>
    <vxe-grid
      ref="table"
      size="small"
      resizable
      auto-resize
      show-footer
      highlight-hover-row
      :columns="tableColumns"
      :height="height"
      :toolbar-config="tableToolbar"
      :cell-class-name="cellClassName"
      :footer-method="() => footerData"
      :edit-config="{ trigger: 'click', mode: 'row' }"
      :edit-rules="editRules"
      @edit-closed="editClosed"
    >
      <template v-slot:toolbar_form>
        <slot name="header"></slot>
      </template>

      <template v-slot:opt="{ row }">
        <i class="el-icon-delete tool-btn" @click="removeRow(row)" />
      </template>

      <template v-slot:displayImage="{ row }">
        <el-image
          :src="$osscdn(row.defaultImageUrl) || '/image/static/img/nopic.jpg'"
          :preview-src-list="[
            $osscdn(row.defaultImageUrl) || '/image/static/img/nopic.jpg',
          ]"
          style="width:60px; height:60px"
        >
          <div slot="error" class="image-slot">
            <img
              src="/image/static/img/nopic.jpg"
              style="width: 60px; height: 60px"
            />
          </div>
        </el-image>
      </template>

      <template v-slot:displayProductName="{ row }">
        <TableLabel :value="row.goodsName" />
        <TableLabel :value="row.goodsNameEn" />
        <TableLabel
          label="SKU"
          labelKey="table_labelKey_evgSkuNo"
          :value="row.evgSkuNo"
        />
        <TableLabel
          label="规格"
          labelKey="table_labelKey_goodsSize"
          :value="row.goodsSize"
        />
        <TableLabel
          label="花型"
          labelKey="table_labelKey_goodsPattern"
          :value="row.goodsPattern"
        />
        <TableLabel
          label="UPC"
          labelKey="table_labelKey_channelEanNo"
          :value="row.channelEanNo"
        />
        <TableLabel
          label="ITEM"
          labelKey="table_labelKey_channelItemNo"
          :value="row.channelItemNo"
        />
        <TableLabel
          label="NC"
          labelKey="table_labelKey_ncNo"
          :value="row.ncNo"
        />
      </template>

      <template v-slot:displayProductUnit="{ row }">
        <TableLabel
          label="单位"
          labelKey="label_key_unit"
          :value="row.v_skuPackSize"
        />
        <TableLabel
          label="入数"
          labelKey="label_key_qty"
          :value="row.specInUnitQty"
        />
      </template>

      <template v-slot:empty>
        <div style="text-align: center">
          <img src="/image/static/img/nodata.png" />
          <p class="no-data mt-1">
            {{ $l('INDEX.table_label_no_data') || '暂无数据！' }}
          </p>
        </div>
      </template>
    </vxe-grid>
  </div>
</template>
<script>
import TableAutoHeight from '@/mixins/table-auto-height'
import EditLoading from '@/mixins/edit-loading'
import { format, multiply, add } from '@/libs/util.number.js'
import tableColumns from './table-columns'
import TableLabel from './label.vue'
export default {
  components: { TableLabel },
  mixins: [TableAutoHeight, EditLoading],
  data() {
    return {
      tableToolbar: {
        slots: { buttons: 'toolbar_form' },
        refresh: { query: () => this.$emit('reload') },
        zoom: true,
        custom: true,
      },
      tableTotal: {},
      tableColumns: tableColumns(this),
      format,
      editRules: {
        unitInCtnQty: [
          {
            type: 'number',
            required: true,
            message: this.$l('table_message_unitInCtnQty') || '请输入装箱入数',
          },
          {
            validator: ({ cellValue }) => {
              if (!cellValue) {
                return new Error(
                  this.$l('table_message_unitInCtnQty_is0') || '装箱入数不能为0'
                )
              }
            },
          },
          {
            validator: ({ row }) => {
              const { specInUnitQty } = row
              if (!specInUnitQty) {
                return new Error(
                  this.$l('table_message_specInUnitQty') ||
                    '该产品未填写规格信息，无法保存'
                )
              }
            },
          },
        ],
      },
    }
  },
  computed: {
    footerData() {
      const result = this.tableColumns
        .filter((c) => c.visible != false)
        .map((t) => {
          if (!t.isTotalColumn) return ''
          const val = this.tableTotal[t.field]
          return val ? format(val) : '0'
        })
      result[3] = this.$l('INDEX.total') || '合计'
      return [result]
    },
    height() {
      const height = this.tableHeight - 100
      return height < 500 ? 500 : height
    },
  },
  watch: {
    footerData() {
      this.$refs.table.updateFooter()
    },
  },
  methods: {
    cellClassName({ column }) {
      return column.property === 'col' ? '' : 'align-top'
    },
    removeRow(row) {
      this.$refs.table.remove(row)
      this.calcTotal()
      this.$emit('change', this.getTableData())
    },
    async insertAt(records, row = -1) {
      const result = await this.$refs.table.insertAt(records, row)
      this.$emit('change', this.getTableData())
      return result
    },
    getTableData() {
      const data = this.$refs.table.getTableData()
      return data?.tableData || []
    },
    async validate() {
      const result = await this.$refs.table.validate(true).catch(() => true)
      return !result
    },
    editClosed({ row }) {
      const { unitInCtnQty, specInUnitQty } = row
      row.specInCtnQty = multiply(unitInCtnQty, specInUnitQty)

      this.calcTotal()

      this.$emit('change', this.getTableData())
    },
    calcTotal() {
      this.tableTotal = {}

      const tableData = this.getTableData()
      if (!tableData?.length) return

      const totalColumnFields = this.tableColumns
        .filter((c) => c.isTotalColumn)
        .map((c) => c.field)
      if (!totalColumnFields?.length) return

      this.tableTotal = tableData.reduce((result, next) => {
        totalColumnFields.forEach((f) => {
          result[f] = add(result[f], next[f])
        })
        return result
      }, Object.fromEntries(totalColumnFields.map((f) => [f, 0])))
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep {
  .vxe-cell {
    .el-input-number {
      margin-top: 10px !important;
    }
  }
  .vxe-table--render-default .vxe-body--row:first-child .vxe-cell--valid {
    top: 50px;
  }
}
</style>
